import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DATA_ACCESS_ALL_STATUS,
  DATA_ACCESS_DISCLAIMER_PLACEHOLDER,
  QueryKeys,
} from "@/constants";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { queryClient } from "@/hooks/use-query-client";
import { sanitizeHtmlWithPlaceholders } from "@/lib/data-access-utils";
import type { DataAccessConfigResponseType } from "@/model/data-access";
import type { Demographics, Relation } from "@/model/demographics";
import { Button } from "@/components/ui/button";
import { useIsRestoring } from "@tanstack/react-query";
import useAuth from "@/context/auth-provider";
import { useDispatch } from "react-redux";
import { useDataAccessUpdateMutation } from "@/hooks/use-data-access";
import { useState } from "react";
import { LoaderCircle } from "lucide-react";
import type { DataAccessUpdatePayload } from "@/types";
import { dequeueAppAlertDialog } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import IDENTIFIERS from "@/constants/test_id";

type AuthAcceptProps = {
  grantedTo: string;
  contentType: string;
};

const AuthAccept: React.FC<AuthAcceptProps> = ({ contentType, grantedTo }) => {
  const { authData } = useAuth();
  const isRestoring = useIsRestoring();
  const dispatch = useDispatch();
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;
  const members = queryClient.getQueryData([QueryKeys.MEMBERS]) as Record<
    string,
    Relation
  >;
  const dataAccessDisclaimerContents = queryClient.getQueryData([
    QueryKeys.DATA_ACCESS_CONFIG,
  ]) as DataAccessConfigResponseType;
  const content =
    contentType &&
    !isRestoring &&
    dataAccessDisclaimerContents.authorization[contentType];
  const placeholderReplacement = !isRestoring
    ? `${members[grantedTo]?.firstName} ${members[grantedTo]?.lastName}`
    : "";
  const dataAccessUpdateMutation = useDataAccessUpdateMutation();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // 3.3.4 Error Prevention (Legal, Financial, Data) — AA / 4.1.3 Status Messages — AA:
  // surfaced and announced on failure instead of the dialog silently closing.
  const [updateError, setUpdateError] = useState<string | null>(null);

  const onAcknowledge = () => {
    setUpdateError(null);
    setIsUpdating(true);
    const updatePayload: DataAccessUpdatePayload = {
      memberId: demographics.memberId,
      grantedBy: demographics.memberId,
      grantedTo,
      status: DATA_ACCESS_ALL_STATUS.ACKNOWLEDGE_ACCEPTED,
      token: authData!.user!.access_token,
    };
    dataAccessUpdateMutation.mutate(
      {
        ...updatePayload,
      },
      {
        onSuccess: () => {
          setIsUpdating(false);
          dispatch(dequeueAppAlertDialog());
        },
        onError: () => {
          setIsUpdating(false);
          setUpdateError(
            "Something went wrong and this wasn't saved. Please try again.",
          );
        },
      },
    );
  };

  return (
    !isRestoring && (
      <AlertDialog
        defaultOpen={true}
        logDialogName={LogSubCategories.ACCESS_ACCEPT_ACKNOWLEDGE}
        logTag={LogTags.MODULE_ACCESS}
        logCategory={LogCategories.DATA_ACCESS_MANAGEMENT}
        logSubCategory={LogSubCategories.ACCESS_ACCEPT_ACKNOWLEDGE}
      >
        <AlertDialogPortal>
          <AlertDialogOverlay className="AlertDialogOverlay" />
          <AlertDialogContent className="AlertDialogContent">
            <AlertDialogTitle className="AlertDialogTitle">
              Disclaimer
            </AlertDialogTitle>
            <AlertDialogDescription className="AlertDialogDescription">
              {content && (
                <span
                  className="text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlWithPlaceholders(
                      content,
                      DATA_ACCESS_DISCLAIMER_PLACEHOLDER.MEMBER_NAME,
                      placeholderReplacement,
                    ),
                  }}
                />
              )}
            </AlertDialogDescription>
            {/* 4.1.3 Status Messages — AA: mounted unconditionally so the error is
                announced the moment it appears, not silently added to an absent region. */}
            {updateError && (
              <p role="alert" className="text-sm text-destructive">
                {updateError}
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <AlertDialogAction
                asChild
                disabled={isUpdating}
                onClick={(event) => {
                  // Keep the dialog open until the mutation actually resolves —
                  // AlertDialogAction closes by default on click.
                  event.preventDefault();
                  onAcknowledge();
                }}
              >
                <Button
                  variant="default"
                  className="w-32 rounded-sm hover:bg-primary-selected"
                  disabled={isUpdating}
                  data-testid={
                    IDENTIFIERS.ACCESS_ACCEPT_DISCLAIMER.ACKNOWLEDGE_BUTTON
                  }
                  logMetadata={{
                    grantedBy: demographics.memberId,
                    grantedTo,
                    status: DATA_ACCESS_ALL_STATUS.ACKNOWLEDGE_ACCEPTED,
                  }}
                >
                  {isUpdating ? (
                    <LoaderCircle className="text-white-500 size-6 animate-spin" />
                  ) : (
                    "Ok"
                  )}
                </Button>
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    )
  );
};

export default AuthAccept;
